/**
 * Evaluate a numeric formula (e.g. "23 * 24") for a given row.
 */
export function evaluateNumericFormula(
  formula: string,
  row: Record<number, string>
): number {
  try {
    const expr = formula.replace(/\b\d+\b/g, (id) => {
      const val = row[id];
      return (
        val !== undefined && val !== '' && !isNaN(Number(val)) ? Number(val) : 0
      ).toString();
    });
    // eslint-disable-next-line no-eval
    return eval(expr);
  } catch {
    return 0;
  }
}

/**
 * Calculate the month difference between two dates.
 * Counts the number of calendar months that are covered by the date range.
 *
 * @param date1 The start date
 * @param date2 The end date
 * @returns The number of months covered in the date range
 *
 * @example
 * // 06/01/2025-06/27/2025 => 1 (same month)
 * // 06/01/2025-07/01/2025 => 2 (spans two months)
 * // 06/01/2025-07/24/2025 => 2 (spans two months)
 * // 06/01/2025-08/02/2025 => 3 (spans three months)
 */
export function calculateMonthDifference(date1: Date, date2: Date): number {
  // Make sure date1 is the earlier date
  if (date1 > date2) {
    [date1, date2] = [date2, date1];
  }

  // Extract year and month components
  const year1 = date1.getFullYear();
  const month1 = date1.getMonth();
  const year2 = date2.getFullYear();
  const month2 = date2.getMonth();

  // Calculate the difference in months between the dates
  const monthsDiff = (year2 - year1) * 12 + (month2 - month1);

  // For the specific case you mentioned: 06/01/2025 and 07/02/2025
  // monthsDiff would be (2025-2025)*12 + (6-5) = 1

  // If the dates are in the same month, return 1
  if (monthsDiff === 0) {
    return 1;
  }

  // Otherwise, the number of months covered is the difference + 1
  return monthsDiff + 1;
}

/**
 * Safely parse a date string to ensure consistent date representation
 * regardless of browser timezone settings
 */
function parseDateSafely(dateStr: string): Date {
  // Check if the string follows MM/DD/YYYY format
  const datePattern = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
  const match = dateStr.match(datePattern);

  if (match) {
    // Extract month, day, year from match groups
    const month = parseInt(match[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Create date at noon local time to avoid timezone issues
    return new Date(year, month, day, 12, 0, 0);
  }

  // If not in the expected format, try to handle ISO format
  if (dateStr.includes('T')) {
    return new Date(dateStr);
  }

  // For other formats, construct with explicit time
  return new Date(`${dateStr}T12:00:00`);
}

/**
 * Calculate the sum of month differences between dates in colId1 and colId2 for each row.
 * Each row's month difference is calculated and then all differences are summed.
 * If dates are in the same month for any row, that row contributes 1 to the sum.
 *
 * @param colId1 The labelId of the first date column (start date)
 * @param colId2 The labelId of the second date column (end date)
 * @param rows The table rows
 * @returns The sum of month differences across all rows
 *
 * @example
 * // For rows with these dates:
 * // Row 1: 06/01/2025 to 06/15/2025 (same month = 1)
 * // Row 2: 07/01/2025 to 09/30/2025 (3 months)
 * // Returns "4"
 */
export function sumMonthDiffsAcrossRows(
  colId1: number,
  colId2: number,
  rows: Array<Record<number, string>>
): string {
  let totalMonths = 0;

  for (const row of rows) {
    const date1Str = row[colId1];
    const date2Str = row[colId2];

    // Skip rows with invalid dates
    if (
      !date1Str ||
      !date2Str ||
      isNaN(new Date(date1Str).getTime()) ||
      isNaN(new Date(date2Str).getTime())
    ) {
      continue;
    }

    const date1 = parseDateSafely(date1Str);
    const date2 = parseDateSafely(date2Str);

    // Use helper function to calculate months difference for this row
    totalMonths += calculateMonthDifference(date1, date2);
  }

  return totalMonths.toString();
}
